import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn.tsx';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting.ts';
import { OrganizationRes } from '@dealstack/shared/lib/generated';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { Flex, Text } from '@mantine/core';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { OrganizationRowActions } from '@dealstack/frontend/app/pages/Organizations/components/OrganizationRowActions/OrganizationRowActions.tsx';
import { StatusBadge } from '@dealstack/frontend/shared/components/StatusBadge/StatusBadge.tsx';

interface Props {
	tOrganization: TranslateFunction<I18Namespaces.ORGANIZATION>;
	onEdit: (organization: OrganizationRes) => void;
	onDelete: (organization: OrganizationRes) => void;
}

export const buildOrganizationsTableColumns = ({
	tOrganization,
	onEdit,
	onDelete,
}: Props): ExtendedColumnDef<OrganizationRes>[] => [
	createTextColumn<OrganizationRes>({
		id: 'name',
		accessorKey: 'name',
		headerText: tOrganization('organizations_management.table.columns.name'),
		enableSorting: true,
	}),
	createTextColumn<OrganizationRes>({
		id: 'contactEmail',
		accessorKey: 'contactEmail',
		headerText: tOrganization('organizations_management.table.columns.contact_email'),
		cell: ({ cell }) => {
			const value = cell.getValue<string | null>();
			return (
				<Text size="B6Medium" c={value ? 'dark.9' : 'dark.0'}>
					{value ?? LONG_DASH}
				</Text>
			);
		},
	}),
	createTextColumn<OrganizationRes>({
		id: 'emailDomains',
		accessorFn: (row) => row.emailDomains?.join(', ') ?? '',
		headerText: tOrganization('organizations_management.table.columns.email_domains'),
		enableSorting: false,
		cell: ({ cell }) => {
			const value = cell.getValue<string>();
			return (
				<Text size="B6Medium" c={value ? 'dark.9' : 'dark.0'}>
					{value || LONG_DASH}
				</Text>
			);
		},
	}),
	createTextColumn<OrganizationRes>({
		id: 'ssoEnabled',
		accessorKey: 'ssoEnabled',
		headerText: tOrganization('organizations_management.table.columns.sso'),
		enableSorting: false,
		cell: ({ cell }) => {
			const enabled = cell.getValue<boolean | undefined>();
			return enabled ? (
				<StatusBadge
					color="teal.8"
					textColor="teal.8"
					text={tOrganization('organizations_management.table.status.enabled')}
				/>
			) : (
				LONG_DASH
			);
		},
	}),
	createTextColumn<OrganizationRes>({
		id: 'auth2FAEnabled',
		accessorKey: 'auth2FAEnabled',
		headerText: tOrganization('organizations_management.table.columns.two_fa'),
		enableSorting: false,
		cell: ({ cell }) => {
			const enabled = cell.getValue<boolean>();
			return enabled ? (
				<StatusBadge
					color="teal.8"
					textColor="teal.8"
					text={tOrganization('organizations_management.table.status.enabled')}
				/>
			) : (
				LONG_DASH
			);
		},
	}),
	{
		id: 'actions',
		header: '',
		enableSorting: false,
		enableColumnFilter: false,
		size: 60,
		cell: ({ row }) => (
			<Flex ml="auto" mr="0" align="center" justify="flex-end" gap="0.5rem">
				<OrganizationRowActions
					onEdit={() => onEdit(row.original)}
					onDelete={() => onDelete(row.original)}
				/>
			</Flex>
		),
	},
];
